import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { api, fileUrl, formatBRL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";

const UF = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

const mask = {
  cpf: (v) => v.replace(/\D/g,"").slice(0,11).replace(/(\d{3})(\d)/,"$1.$2").replace(/(\d{3})(\d)/,"$1.$2").replace(/(\d{3})(\d{1,2})$/,"$1-$2"),
  cep: (v) => v.replace(/\D/g,"").slice(0,8).replace(/(\d{5})(\d)/,"$1-$2"),
  phone: (v) => v.replace(/\D/g,"").slice(0,11).replace(/(\d{2})(\d)/,"($1) $2").replace(/(\d{5})(\d)/,"$1-$2"),
};

export default function CheckoutPage() {
  const { items, subtotal, clear } = useCart();
  const [settings, setSettings] = useState(null);
  const nav = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "", whatsapp: "", email: "", cpf: "",
    cep: "", street: "", number: "", complement: "", neighborhood: "", city: "", state: "SP",
    payment_method: "pix", notes: "",
  });

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data));
  }, []);

  useEffect(() => {
    if (items.length === 0 && !submitting) {
      // allow brief access, redirect
    }
  }, [items, submitting]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: typeof e === "string" ? e : e.target.value }));
  const updMasked = (k, m) => (e) => setForm((f) => ({ ...f, [k]: mask[m](e.target.value) }));

  const freeShipping = settings && subtotal >= (settings.free_shipping_threshold || 0);
  const shipping = freeShipping ? 0 : (settings?.default_shipping || 0);
  const total = subtotal + shipping;

  const lookupCep = async () => {
    const cep = form.cep.replace(/\D/g, "");
    if (cep.length !== 8) return;
    try {
      const r = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const d = await r.json();
      if (d && !d.erro) {
        setForm((f) => ({
          ...f,
          street: d.logradouro || f.street,
          neighborhood: d.bairro || f.neighborhood,
          city: d.localidade || f.city,
          state: d.uf || f.state,
        }));
      }
    } catch {}
  };

  const submit = async (e) => {
    e.preventDefault();
    const required = ["name","whatsapp","email","cpf","cep","street","number","neighborhood","city","state"];
    for (const k of required) {
      if (!form[k]) {
        toast.error("Preencha todos os campos obrigatórios");
        return;
      }
    }
    if (items.length === 0) {
      toast.error("Seu carrinho está vazio");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/checkout", {
        customer: { name: form.name, whatsapp: form.whatsapp, email: form.email, cpf: form.cpf },
        address: { cep: form.cep, street: form.street, number: form.number, complement: form.complement, neighborhood: form.neighborhood, city: form.city, state: form.state },
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, size: i.size, color: i.color })),
        payment_method: form.payment_method,
        notes: form.notes,
      });
      clear();
      nav(`/pedido/${data.code}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao finalizar pedido");
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0 && !submitting) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-24 text-center">
        <h1 className="font-display text-3xl text-white tracking-tighter">Carrinho vazio</h1>
        <p className="text-zinc-400 mt-3">Adicione produtos antes de finalizar.</p>
        <Button onClick={() => nav("/")} className="mt-6 bg-white text-black">Voltar à loja</Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-5 lg:px-8 py-14">
      <div className="overline-label">Pedido</div>
      <h1 className="font-display text-5xl tracking-tighter text-white mt-3">Checkout</h1>

      <form onSubmit={submit} className="grid lg:grid-cols-3 gap-10 mt-10">
        <div className="lg:col-span-2 space-y-10">
          <Section title="Seus dados">
            <Field label="Nome completo *"><Input data-testid="chk-name" value={form.name} onChange={upd("name")} className="bg-[#0A0A0C] border-white/15 h-11 text-white" /></Field>
            <Field label="WhatsApp *"><Input data-testid="chk-whatsapp" value={form.whatsapp} onChange={updMasked("whatsapp","phone")} placeholder="(00) 00000-0000" className="bg-[#0A0A0C] border-white/15 h-11 text-white" /></Field>
            <Field label="E-mail *"><Input data-testid="chk-email" type="email" value={form.email} onChange={upd("email")} className="bg-[#0A0A0C] border-white/15 h-11 text-white" /></Field>
            <Field label="CPF *"><Input data-testid="chk-cpf" value={form.cpf} onChange={updMasked("cpf","cpf")} placeholder="000.000.000-00" className="bg-[#0A0A0C] border-white/15 h-11 text-white" /></Field>
          </Section>

          <Section title="Endereço de entrega">
            <Field label="CEP *">
              <Input data-testid="chk-cep" value={form.cep} onChange={updMasked("cep","cep")} onBlur={lookupCep} placeholder="00000-000" className="bg-[#0A0A0C] border-white/15 h-11 text-white" />
            </Field>
            <Field label="Estado *">
              <Select value={form.state} onValueChange={(v) => setForm((f) => ({ ...f, state: v }))}>
                <SelectTrigger className="bg-[#0A0A0C] border-white/15 h-11 text-white" data-testid="chk-state"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0A0A0C] border-white/15 text-white">
                  {UF.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Cidade *"><Input data-testid="chk-city" value={form.city} onChange={upd("city")} className="bg-[#0A0A0C] border-white/15 h-11 text-white" /></Field>
            <Field label="Bairro *"><Input data-testid="chk-neighborhood" value={form.neighborhood} onChange={upd("neighborhood")} className="bg-[#0A0A0C] border-white/15 h-11 text-white" /></Field>
            <Field label="Endereço *" wide><Input data-testid="chk-street" value={form.street} onChange={upd("street")} className="bg-[#0A0A0C] border-white/15 h-11 text-white" /></Field>
            <Field label="Número *"><Input data-testid="chk-number" value={form.number} onChange={upd("number")} className="bg-[#0A0A0C] border-white/15 h-11 text-white" /></Field>
            <Field label="Complemento"><Input data-testid="chk-complement" value={form.complement} onChange={upd("complement")} className="bg-[#0A0A0C] border-white/15 h-11 text-white" /></Field>
          </Section>

          <Section title="Pagamento">
            <div className="col-span-2">
              <RadioGroup value={form.payment_method} onValueChange={(v) => setForm((f) => ({ ...f, payment_method: v }))} className="grid sm:grid-cols-2 gap-3">
                <label className={`border p-5 cursor-pointer transition-colors ${form.payment_method === "pix" ? "border-white bg-white/5" : "border-white/15"}`}>
                  <div className="flex items-center gap-3">
                    <RadioGroupItem value="pix" id="pix" data-testid="pay-pix" />
                    <div>
                      <div className="font-display text-lg text-white">Pix</div>
                      <div className="text-xs text-zinc-400 mt-1">Aprovação imediata — 5% off*</div>
                    </div>
                  </div>
                </label>
                <label className={`border p-5 cursor-pointer transition-colors ${form.payment_method === "credit_card" ? "border-white bg-white/5" : "border-white/15"}`}>
                  <div className="flex items-center gap-3">
                    <RadioGroupItem value="credit_card" id="cc" data-testid="pay-cc" />
                    <div>
                      <div className="font-display text-lg text-white">Cartão de Crédito</div>
                      <div className="text-xs text-zinc-400 mt-1">Em até 6x sem juros</div>
                    </div>
                  </div>
                </label>
              </RadioGroup>
              <p className="text-xs text-zinc-500 mt-3">*A loja opera em modo demonstração. Integre o Mercado Pago pelo painel para processar pagamentos reais.</p>
            </div>
          </Section>
        </div>

        <aside className="border border-white/10 p-6 h-fit lg:sticky lg:top-24 space-y-5">
          <div className="overline-label">Resumo</div>
          <div className="space-y-3 max-h-72 overflow-y-auto">
            {items.map((it) => {
              const key = `${it.product_id}|${it.size || ""}|${it.color || ""}`;
              return (
                <div key={key} className="flex gap-3 text-sm">
                  <div className="shrink-0 h-16 w-14 bg-black overflow-hidden border border-white/5">
                    {it.image ? <img src={fileUrl(it.image)} alt="" className="w-full h-full object-cover" /> : null}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white truncate">{it.name}</div>
                    <div className="text-xs text-zinc-500">{it.quantity} × {formatBRL(it.price)}</div>
                  </div>
                  <div className="text-white font-display">{formatBRL(it.price * it.quantity)}</div>
                </div>
              );
            })}
          </div>
          <div className="border-t border-white/10 pt-4 space-y-2 text-sm">
            <div className="flex justify-between text-zinc-300"><span>Subtotal</span><span>{formatBRL(subtotal)}</span></div>
            <div className="flex justify-between text-zinc-300">
              <span>Frete</span>
              <span className={freeShipping ? "text-emerald-400" : ""}>{freeShipping ? "Grátis" : formatBRL(shipping)}</span>
            </div>
            <div className="flex justify-between items-baseline pt-3 border-t border-white/10 mt-3">
              <span className="text-zinc-200">Total</span>
              <span className="font-display text-2xl text-white" data-testid="chk-total">{formatBRL(total)}</span>
            </div>
          </div>
          <Button type="submit" disabled={submitting} data-testid="chk-submit-btn" className="w-full h-13 bg-white text-black hover:bg-zinc-200 uppercase tracking-widest text-xs">
            {submitting ? "Enviando..." : "Finalizar pedido"}
          </Button>
          <p className="text-[11px] text-zinc-500 text-center">Ao confirmar você aceita nossa política de privacidade.</p>
        </aside>
      </form>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-white/10 p-6 lg:p-8">
      <h2 className="font-display text-2xl text-white tracking-tight mb-6">{title}</h2>
      <div className="grid sm:grid-cols-2 gap-5">{children}</div>
    </div>
  );
}
function Field({ label, children, wide }) {
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <Label className="text-xs text-zinc-400 mb-2 block tracking-wide">{label}</Label>
      {children}
    </div>
  );
}
